/*
 * B2B minimum-order guard for the product page buy buttons.
 *
 * Reuses the exact rule-matching logic assets/b2b-cart-progress.js already
 * built for the cart drawer bar (window.themeB2BCartProgress) instead of
 * re-reading window.BSS_B2B.amoRules a second time. All it adds is a way to
 * ask: "if I add `qty` of this product, does it clear every minimum-order
 * rule it matches?" — folding the product into the shopper's current cart
 * lines and running the same evaluateRule() the bar uses.
 *
 * UX note: this never disables the "Añadir al carrito" button (disabled
 * buttons lose focus/AT and read as broken, not "not yet available" — see
 * NN/G). It only decides, at click time, whether to let the request go out
 * or show the reason inline instead — same accessible error slot the theme
 * already uses for real /cart/add.js errors.
 *
 * Accelerated checkout (Shop Pay/"Comprar ahora"/PayPal) is a separate
 * decision from the inline hint: it stays hidden for the entire time a
 * minimum-order rule governs this product for this shopper, met or not —
 * see the comment on syncAcceleratedCheckout() for why "already met" isn't
 * good enough to bring it back.
 */
(function () {
  'use strict';

  function readJsonScript(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  }

  /**
   * `amountTemplate`/`quantityTemplate` are passed in rather than read off
   * `strings` directly because the wording differs by what's actually being
   * described: "si agregas este producto..." only makes sense for a rule
   * THIS product affects — reusing it for a different, unrelated rule
   * elsewhere in the cart (see the "Coronas" case in evaluate()) would
   * imply adding this product helps clear that other rule, when it can't.
   */
  function unmetMessage(progress, entry, moneyFormat, amountTemplate, quantityTemplate) {
    // Custom per-rule copy (assets/b2b-cart-progress.js RULE_MESSAGES) states
    // the rule's flat threshold, not "if you add this product" — same
    // sentence regardless of whether this is the preview or the real-state
    // template, so it wins over both before falling back to them.
    var custom = progress.customRuleMessage(entry, moneyFormat);
    if (custom) return custom;

    return entry.isAmount
      ? progress.interpolate(amountTemplate, {
          amount: progress.formatMoney(entry.missing * 100, moneyFormat),
          minimum: progress.formatMoney(entry.rule.minimum * 100, moneyFormat),
          scope: entry.scope.join(', '),
        })
      : progress.interpolate(quantityTemplate, {
          count: String(Math.ceil(entry.missing)),
          minimum: String(entry.rule.minimum),
          scope: entry.scope.join(', '),
        });
  }

  /**
   * `root` is the .x-block-buy-buttons container (x-data="xProductCart(...)")
   * so the product context script lives right next to the buttons it guards.
   *
   * Returns null only when no minimum-order rule governs this product for
   * this shopper at all (retail shoppers, or a rule whose collections don't
   * cover this product). Otherwise always returns a state object with
   * `messages` — the shortfall while unmet, a confirmation once every rule
   * THIS product governs is met (`met: true`), so callers that care about the
   * rule's mere existence regardless of status (accelerated checkout) have
   * `governed` to key off instead.
   *
   * The confirmation is careful not to overclaim: a shopper can be clear of
   * every rule this product touches while a *different* rule elsewhere in
   * their cart (a different collection, e.g. a "Coronas" minimum) is still
   * unmet — adding this product doesn't affect that rule at all, so "ya
   * puedes continuar tu compra" would be false. `ready` (and the message
   * that goes with it) only turns true once every minimum-order rule that
   * actually applies to something already in the cart — not just the ones
   * this product happens to share a collection with — is satisfied.
   */
  function evaluate(root, qty) {
    var progress = window.themeB2BCartProgress;
    if (!progress || !root) return null;

    var productContext = readJsonScript(root.querySelector('[data-b2b-product-context]'));
    var cartContext = readJsonScript(document.querySelector('[data-b2b-min-order-context]'));
    if (!productContext || !cartContext) return null;

    // From the live session, not from cartContext: that blob re-renders with
    // its section, and section markup never branches on `customer` here.
    var customerTags = window.themeB2BSession ? window.themeB2BSession.tags : [];
    if (!customerTags.length) return null; // retail shoppers never see this

    var rules = progress.readMinimumRules(customerTags);
    if (!rules.length) return null;

    var productCollectionIds = (productContext.collections || []).map(function (collection) {
      return String(collection.id);
    });

    // A rule that doesn't govern this product's collections must never
    // affect whether THIS PRODUCT's own shortfall shows, whether that rule
    // is met or unmet elsewhere in the cart.
    var matchingRules = rules.filter(function (rule) {
      return productCollectionIds.some(function (id) {
        return rule.collections[id] === true;
      });
    });
    if (!matchingRules.length) return null;

    var quantity = Number(qty) || 0;
    var candidateLine = {
      quantity: quantity,
      linePrice: Math.round(productContext.price * quantity),
      collections: productContext.collections || [],
    };
    var lines = (cartContext.lines || []).concat([candidateLine]);

    var evaluated = matchingRules
      .map(function (rule) {
        return progress.evaluateRule(rule, lines);
      })
      .filter(Boolean);
    var pending = evaluated.filter(function (entry) {
      return !entry.met;
    });

    if (pending.length) {
      // Unmet on this product's own rule(s) always takes priority — the
      // shopper is looking at this page to decide whether to add it, so
      // that's what the hint has to answer first.
      return {
        governed: true,
        met: false,
        messages: pending.map(function (entry) {
          return unmetMessage(
            progress,
            entry,
            cartContext.moneyFormat,
            cartContext.strings.minAmount,
            cartContext.strings.minQuantity
          );
        }),
      };
    }

    // Every rule THIS product touches is met. Before confirming the shopper
    // is clear to check out, also check every OTHER minimum-order rule this
    // customer is subject to, against the same (cart + candidate) lines —
    // evaluateRule() already returns null for a rule that matches nothing
    // actually in that set, so an unrelated collection the shopper hasn't
    // touched at all never counts against them here.
    var otherRules = rules.filter(function (rule) {
      return matchingRules.indexOf(rule) === -1;
    });
    var otherPending = otherRules
      .map(function (rule) {
        return progress.evaluateRule(rule, lines);
      })
      .filter(function (entry) {
        return entry && !entry.met;
      });

    var messages = evaluated.map(function (entry) {
      return progress.interpolate(cartContext.strings.minOrderMet, {
        minimum: entry.isAmount
          ? progress.formatMoney(entry.rule.minimum * 100, cartContext.moneyFormat)
          : String(entry.rule.minimum),
        scope: entry.scope.join(', '),
      });
    });

    if (otherPending.length) {
      // Confirm this scope, but don't call the shopper "ready" — say
      // exactly what's still missing elsewhere instead of staying silent
      // about it (same reasoning as the cart bar: every blocking minimum
      // gets named, not just the closest one).
      messages = messages.concat(
        otherPending.map(function (entry) {
          // Plain wording here, not the "si agregas este producto" preview —
          // this rule is about a different collection this product isn't
          // in, so the shortfall is the cart's real state, same phrasing
          // the cart bar itself uses for it.
          return unmetMessage(
            progress,
            entry,
            cartContext.moneyFormat,
            cartContext.strings.minAmountReal,
            cartContext.strings.minQuantityReal
          );
        })
      );
    } else {
      messages.push(cartContext.strings.minOrderReady);
    }

    return {
      governed: true,
      met: otherPending.length === 0, // truly ready to check out, not just clear of this product's own rule
      messages: messages,
    };
  }

  /* ------------------------------------------------------- inline nudge */

  function currentQty(root) {
    var input = root.querySelector('input[name="quantity"]');
    return input ? input.value : 1;
  }

  function renderHint(root, state) {
    var hint = root.querySelector('[data-b2b-min-order-hint]');
    if (!hint) return;

    if (!state || !state.messages.length) {
      hint.hidden = true;
      hint.innerHTML = '';
      return;
    }
    // Color communicates the same status as the copy: red while short,
    // green once cleared. Classes come from the two data-color-* attributes
    // in Liquid rather than being hardcoded here, so the actual token stays
    // themeable in one place (snippets/product-template.liquid).
    var unmetClass = hint.dataset.colorUnmet;
    var metClass = hint.dataset.colorMet;
    if (unmetClass) hint.classList.remove(unmetClass);
    if (metClass) hint.classList.remove(metClass);
    var activeClass = state.met ? metClass : unmetClass;
    if (activeClass) hint.classList.add(activeClass);

    // Trusted markup: strings.minAmount/minQuantity/minOrderMet come from
    // the shop's own locale JSON (general.cart.b2b_min_*_html), same as the
    // cart bar's messages.innerHTML in b2b-cart-progress.js — never user
    // input.
    hint.innerHTML = state.messages.join(' · ');
    hint.hidden = false;
  }

  /*
   * Accelerated checkout ("Comprar ahora" / Shop Pay / PayPal) goes straight
   * to checkout, skipping the cart by design — Shopify documents that these
   * buttons don't support cart-level validation at all. BSS tries to guard
   * its own instrumented copy of the button, but that guard is storefront
   * JS and we confirmed live that an un-instrumented duplicate slips past it.
   *
   * So for any wholesale shopper a minimum-order rule governs on this
   * product, accelerated checkout is removed as an option entirely — not
   * just while the minimum is unmet. Bringing it back the moment the rule
   * is met would (a) reintroduce the exact one-tap bypass this guard exists
   * to close the instant the shopper's cart crosses the threshold, and
   * (b) make the button flicker in and out as they add/remove items, which
   * is the actual business ask: express checkout doesn't make sense at all
   * for an account bound by these rules, so it stays off the table and
   * "Añadir al carrito" is the only path, full stop.
   *
   * This costs nothing in conversion for a shopper below the minimum (they
   * could not have checked out through it anyway) and, for one at/above it,
   * simply routes them through the cart instead — where the theme's own
   * cart/checkout flow already applies BSS's wholesale pricing correctly.
   * "Añadir al carrito" stays fully available and clickable regardless (see
   * the UX note at the top of this file).
   *
   * NOTE: this is still storefront JS, i.e. presentation-level enforcement.
   * It closes the practical hole, not a determined bypass via devtools. Real
   * server-side enforcement that also covers express checkouts requires a
   * Shopify Cart and Checkout Validation Function, which lives in an app,
   * not in this theme.
   *
   * No stand-in text takes its place — an earlier version swapped in an
   * explanatory placeholder ("Compra directa disponible al alcanzar el
   * mínimo") sized to match the button it replaced, but that read as its own
   * confusing leftover once it stopped being conditional on "unmet" (a
   * wholesale shopper who is well past every minimum still saw it, since it
   * has nothing left to explain). Removed per explicit call: the space is
   * just empty.
   */
  function syncAcceleratedCheckout(root, state) {
    root.querySelectorAll('.payment-button-container').forEach(function (container) {
      var blocked = !!(state && state.governed);

      if (blocked) {
        if (!container.dataset.b2bHiddenByGuard) {
          container.dataset.b2bHiddenByGuard = 'true';
          container.dataset.b2bPrevDisplay = container.style.display || '';
        }
        container.style.display = 'none';
      } else if (container.dataset.b2bHiddenByGuard) {
        container.style.display = container.dataset.b2bPrevDisplay || '';
        delete container.dataset.b2bHiddenByGuard;
        delete container.dataset.b2bPrevDisplay;
      }

      // display:none already removes these from the tab order, but the
      // buttons are also driven by app JS that can act on them directly,
      // so make them inert rather than merely invisible.
      container.querySelectorAll('.shopify-payment-button__button').forEach(function (btn) {
        if (blocked) {
          if (!btn.dataset.b2bBlockedByGuard) btn.dataset.b2bBlockedByGuard = 'true';
          btn.disabled = true;
          btn.setAttribute('tabindex', '-1');
          btn.setAttribute('aria-hidden', 'true');
        } else if (btn.dataset.b2bBlockedByGuard) {
          delete btn.dataset.b2bBlockedByGuard;
          btn.disabled = false;
          btn.removeAttribute('tabindex');
          btn.removeAttribute('aria-hidden');
        }
      });
    });
  }

  function applyGuardState(root) {
    var state = evaluate(root, currentQty(root));
    renderHint(root, state);
    syncAcceleratedCheckout(root, state);
  }

  /*
   * [data-b2b-min-order-context] is Liquid output: accurate for the cart at
   * the moment this page was requested, then frozen. "Añadir al carrito" is
   * an AJAX request — no page reload — so after adding an item (this one or
   * any other) that script tag still describes the pre-add cart, and every
   * evaluate() call keeps folding the candidate onto stale lines. Confirmed
   * live: the hint stops moving after the first add while the cart drawer's
   * own bar (which the Section Rendering API re-renders fresh each time)
   * keeps updating right next to it — the exact mismatch that read as "the
   * math is wrong" when it was really "the input is old".
   *
   * Fixed the same way assets/theme-editor-alpine-custom.js's
   * xUpdateVariantQuanity store already keeps other PDP-only bits in sync
   * after a cart change: re-fetch this section via the Section Rendering API
   * and swap in the fresh markup. Scoped to the #x-b2b-min-order-{sectionId}
   * wrapper alone (just the cart-lines script + the hint paragraph) rather
   * than reusing that store, since this page may have no quantity_selector
   * or volume_pricing_table block for it to key off — see snippets/product-template.liquid.
   */
  function refreshCartContext(root, onDone) {
    var wrapper = root.querySelector('[data-b2b-section-id]');
    if (!wrapper) {
      onDone();
      return;
    }
    var sectionId = wrapper.dataset.b2bSectionId;
    if (!sectionId) {
      onDone();
      return;
    }

    var url;
    try {
      url = new URL(window.location.href);
    } catch (error) {
      onDone();
      return;
    }
    url.searchParams.set('section_id', sectionId);

    fetch(url.toString())
      .then(function (response) {
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = wrapper.id ? parsed.getElementById(wrapper.id) : null;
        if (fresh) wrapper.innerHTML = fresh.innerHTML;
      })
      .catch(function () {
        // Best effort: the hint just keeps showing its last known state
        // (possibly still stale) until the next successful refresh.
      })
      .then(onDone, onDone);
  }

  function watchHints() {
    document.querySelectorAll('[data-b2b-product-context]').forEach(function (script) {
      var root = script.closest('.x-block-buy-buttons');
      if (!root) return;
      applyGuardState(root);

      // Typing into the quantity input fires a real 'input' event — cheap
      // to also handle directly instead of waiting for the next poll tick.
      root.addEventListener('input', function (event) {
        if (event.target && event.target.name === 'quantity') applyGuardState(root);
      });

      // Any cart mutation on the page (this product, a different one, the
      // cart drawer's own +/-, quick view, bundles, ...) can change what this
      // hint should say, so re-fetch on all of them rather than only after
      // this root's own "Añadir al carrito" — see refreshCartContext() above.
      document.addEventListener('eurus:cart:items-changed', function () {
        refreshCartContext(root, function () {
          applyGuardState(root);
        });
      });

      // The +/- buttons set Alpine's `qty` in JS (assets/quantity-selector.js),
      // which never fires a DOM 'input' event. That file also dispatches a
      // `quantity:changed-{sectionId}` event we could listen for instead, but
      // its updatePrice() throws before reaching that dispatch whenever
      // `.target-price` isn't in the DOM (pre-existing bug, unrelated to this
      // guard) — so the event isn't reliable here. A light poll on the
      // input's own value sidesteps that entirely.
      // Also re-applied unconditionally on each tick, not only when the
      // quantity changed: Shopify re-renders the accelerated checkout button
      // asynchronously, so a container hidden here can come back on its own.
      var lastQty = currentQty(root);
      setInterval(function () {
        var qty = currentQty(root);
        if (qty !== lastQty) lastQty = qty;
        applyGuardState(root);
      }, 400);
    });
  }

  /*
   * There used to be an "orphan checkout button" neutralizer here: it
   * disabled the plain Shopify buy-now button whenever BSS B2B's instrumented
   * copy (`bss-b2b-btn-buyitnow`) was present, to stop an un-instrumented
   * duplicate from skipping BSS's minimum-order check. Removed because:
   *
   * - syncAcceleratedCheckout() above already hides and disables every
   *   accelerated checkout button for any shopper a minimum-order rule
   *   governs, so the neutralizer no longer protected anything extra.
   * - BSS hands the click off to that plain button (it hides its own copy,
   *   shows the one tagged `bss-b2b-default-buy-now`, and forwards the click)
   *   when its check doesn't apply. Confirmed live for a retail shopper: with
   *   the button still disabled at that instant, the first "Comprar ahora"
   *   click did nothing, and before that it stayed greyed out for good.
   */

  function watchB2B() {
    watchHints();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchB2B);
  } else {
    watchB2B();
  }

  window.themeB2BMinOrderGuard = { evaluate: evaluate };
})();
